import React, { useState, useRef, useEffect } from 'react';
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';

const HtmlBeautifierPage = () => {
  const [htmlInput, setHtmlInput] = useState('');
  const [formattedHtml, setFormattedHtml] = useState('');
  const [error, setError] = useState('');
  const [tabSize, setTabSize] = useState(2);
  const [copySuccess, setCopySuccess] = useState(false);
  const fileInputRef = useRef(null);
  
  // Refs for scroll syncing
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const inputLineNumbersRef = useRef(null);
  const outputLineNumbersRef = useRef(null);
  const [isInputScrolling, setIsInputScrolling] = useState(false);
  const [isOutputScrolling, setIsOutputScrolling] = useState(false);
  
  // Enhanced scroll synchronization
  const handleInputScroll = () => {
    if (inputLineNumbersRef.current && inputRef.current) {
      inputLineNumbersRef.current.scrollTop = inputRef.current.scrollTop;
    }

    if (!isOutputScrolling && outputRef.current && inputRef.current) {
      setIsInputScrolling(true);
      const inputScrollTop = inputRef.current.scrollTop;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const scrollRatio = inputScrollTop / (inputScrollHeight - inputClientHeight || 1);
      const outputScrollTop = scrollRatio * (outputScrollHeight - outputClientHeight || 1);
      
      outputRef.current.scrollTop = outputScrollTop;
      
      if (outputLineNumbersRef.current) {
        outputLineNumbersRef.current.scrollTop = outputScrollTop;
      }
      
      setTimeout(() => setIsInputScrolling(false), 50);
    }
  };

  const handleOutputScroll = () => {
    if (outputLineNumbersRef.current && outputRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }

    if (!isInputScrolling && inputRef.current && outputRef.current) {
      setIsOutputScrolling(true);
      const outputScrollTop = outputRef.current.scrollTop;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const scrollRatio = outputScrollTop / (outputScrollHeight - outputClientHeight || 1);
      const inputScrollTop = scrollRatio * (inputScrollHeight - inputClientHeight || 1);
      
      inputRef.current.scrollTop = inputScrollTop;
      
      if (inputLineNumbersRef.current) {
        inputLineNumbersRef.current.scrollTop = inputScrollTop;
      }
      
      setTimeout(() => setIsOutputScrolling(false), 50);
    }
  };
  
  // Function to beautify HTML
  const beautifyHtml = () => {
    if (!htmlInput.trim()) {
      setError('Please enter HTML code to beautify.');
      setFormattedHtml('');
      return;
    }
    
    try {
      setError('');
      
      // Format the HTML
      const formatted = prettifyHtml(htmlInput, tabSize);
      setFormattedHtml(formatted);
    } catch (e) {
      setError('Error formatting HTML: ' + e.message);
      setFormattedHtml('');
    }
  };
  
  // List of self-closing (void) HTML elements
  const voidElements = new Set([
    'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 
    'link', 'meta', 'param', 'source', 'track', 'wbr'
  ]);
  
  // New improved HTML formatting function
  const prettifyHtml = (html, indentSize) => {
    // Remove existing whitespace between tags
    html = html.replace(/>\s+</g, '><');
    
    const tokens = [];
    let current = 0;
    let result = '';
    let indentLevel = 0;
    
    // Function to add indentation
    const indent = () => ' '.repeat(indentLevel * indentSize);
    
    // Tokenize the HTML
    while (current < html.length) {
      // Handle HTML comments
      if (html.substring(current, current + 4) === '<!--') {
        let endComment = html.indexOf('-->', current);
        if (endComment === -1) endComment = html.length;
        
        tokens.push({
          type: 'comment',
          content: html.substring(current, endComment + 3)
        });
        
        current = endComment + 3;
        continue;
      }
      
      // Handle doctype
      if (html.substring(current, current + 9).toLowerCase() === '<!doctype') {
        let endDoctype = html.indexOf('>', current);
        if (endDoctype === -1) endDoctype = html.length;
        
        tokens.push({
          type: 'doctype',
          content: html.substring(current, endDoctype + 1)
        });
        
        current = endDoctype + 1;
        continue;
      }
      
      // Handle opening tags
      if (html[current] === '<' && html[current + 1] !== '/') {
        let endTag = html.indexOf('>', current);
        if (endTag === -1) endTag = html.length;
        
        const tagContent = html.substring(current, endTag + 1);
        const tagMatch = tagContent.match(/<([^\s>]+)/);
        const tagName = tagMatch ? tagMatch[1].toLowerCase() : '';
        const isSelfClosing = tagContent.endsWith('/>') || voidElements.has(tagName);
        
        tokens.push({
          type: 'openTag',
          content: tagContent,
          tagName: tagName,
          isSelfClosing: isSelfClosing
        });
        
        current = endTag + 1;
        continue;
      }
      
      // Handle closing tags
      if (html.substring(current, current + 2) === '</') {
        let endTag = html.indexOf('>', current);
        if (endTag === -1) endTag = html.length;
        
        const tagContent = html.substring(current, endTag + 1);
        const tagMatch = tagContent.match(/<\/([^\s>]+)/);
        const tagName = tagMatch ? tagMatch[1].toLowerCase() : '';
        
        tokens.push({
          type: 'closeTag',
          content: tagContent,
          tagName: tagName
        });
        
        current = endTag + 1;
        continue;
      }
      
      // Handle text content
      let endText = html.indexOf('<', current);
      if (endText === -1) endText = html.length;
      
      const textContent = html.substring(current, endText).trim();
      if (textContent) {
        tokens.push({
          type: 'text',
          content: textContent
        });
      }
      
      current = endText;
    }
    
    // Build the formatted HTML
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      
      switch (token.type) {
        case 'doctype':
          result += token.content + '\n';
          break;
        
        case 'comment':
          result += indent() + token.content + '\n';
          break;
        
        case 'openTag':
          result += indent() + token.content;
          
          // Don't add newline for inline elements with text
          const nextToken = tokens[i + 1];
          const nextNextToken = tokens[i + 2];
          
          // Check if this is a simple tag-text-close sequence (inline content)
          const isInlineContent = (
            nextToken && nextToken.type === 'text' && 
            nextNextToken && nextNextToken.type === 'closeTag' && 
            nextNextToken.tagName === token.tagName
          );
          
          if (!token.isSelfClosing && !isInlineContent) {
            indentLevel++;
          }
          
          if (isInlineContent) {
            result += nextToken.content;
            i++; // Skip the text token since we already added it
          } else {
            result += '\n';
          }
          break;
        
        case 'closeTag':
          // Only reduce indent for standard closing tags
          if (i > 0 && tokens[i - 1].type !== 'text') {
            indentLevel = Math.max(0, indentLevel - 1);
          }
          
          // If previous token is text and its parent is an opening tag, don't indent
          const prevToken = tokens[i - 1];
          if (prevToken && prevToken.type === 'text') {
            result += token.content + '\n';
          } else {
            result += indent() + token.content + '\n';
          }
          break;
        
        case 'text':
          // Only add text if it's not already handled with an inline tag
          if (i > 0 && tokens[i - 1].type === 'openTag' && 
              i + 1 < tokens.length && tokens[i + 1].type === 'closeTag') {
            // Skip - this will be handled by the openTag case
          } else {
            // Format text content but preserve internal whitespace
            result += indent() + token.content + '\n';
          }
          break;
      }
    }
    
    // Remove trailing newlines and unnecessary whitespace
    return result.trim();
  };
  
  const minifyHtml = () => {
    if (!htmlInput.trim()) {
      setError('Please enter HTML code to minify.');
      setFormattedHtml('');
      return;
    }
    
    try {
      setError('');
      
      // Simple minification: remove whitespace between tags and collapse internal whitespace
      let minified = htmlInput
        .replace(/>\s+</g, '><') // Remove whitespace between tags
        .replace(/\s{2,}/g, ' ') // Collapse multiple spaces to single space
        .replace(/\n/g, '') // Remove newlines
        .replace(/\t/g, '') // Remove tabs
        .replace(/\s+</g, '<') // Remove whitespace before opening tags
        .replace(/>\s+/g, '>') // Remove whitespace after closing tags
        .trim();
      
      setFormattedHtml(minified);
    } catch (e) {
      setError('Error minifying HTML: ' + e.message);
    }
  };

  const unescapeSmartQuotes = () => {
    if (!htmlInput) {
      setError('Please enter HTML code to process.');
      return;
    }

    setError('');
    const unescaped = htmlInput.replace(/\\([“”])/g, '$1');
    setHtmlInput(unescaped);
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setHtmlInput(e.target.result);
      };
      reader.readAsText(file);
    }
  };
  
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  
  const downloadFormattedHtml = () => {
    try {
      const content = formattedHtml || htmlInput;
      const blob = new Blob([content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'formatted-html.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Error downloading: ' + e.message);
    }
  };
  
  const handleCopyOutput = () => {
    navigator.clipboard.writeText(formattedHtml).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      setError('Failed to copy text: ' + err.message);
    });
  };
  
  const loadExample = () => {
    setHtmlInput(`<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>HTML Example</title><style>body{font-family:Arial,sans-serif;margin:0;padding:20px}h1{color:#333}</style></head><body><header><h1>Welcome to HTML Beautifier</h1><nav><ul><li><a href="#">Home</a></li><li><a href="#">About</a></li><li><a href="#">Contact</a></li></ul></nav></header><main><section><h2>What is HTML Beautifier?</h2><p>HTML Beautifier is a tool that formats and indents your HTML code to make it more readable.</p><div class="features"><ul><li>Easy to use</li><li>Free online tool</li><li>Supports all HTML tags</li></ul></div></section><section><h2>How to Use</h2><ol><li>Paste your HTML code</li><li>Click the Beautify button</li><li>Copy the formatted result</li></ol></section></main><footer><p>&copy; 2023 HTML Tools</p></footer></body></html>`);
  };
  
  const clearAll = () => {
    setHtmlInput('');
    setFormattedHtml('');
    setError('');
  };
  
  return (
    <div>
      <SEO 
        title="HTML Beautifier - Format and Indent HTML Code Online" 
        description="Free online HTML beautifier tool to format, indent, and make your HTML code more readable. Clean up messy HTML with proper indentation."
        keywords="html beautifier, format html, html formatter, indent html, html pretty print, beautify html"
      />
      
      <div className="container" style={{ maxWidth: '%95', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>HTML Beautifier</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Format and indent your HTML code to make it more readable. Clean up messy HTML with proper indentation.
        </p>
        

        <div className="main-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="controls" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center', marginBottom: '10px' }}>
            <div className="button-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <button 
                onClick={beautifyHtml}
                style={{ 
                  padding: '8px 16px', 
                  background: '#4CAF50', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Beautify HTML
              </button>
              
              <button 
                onClick={minifyHtml}
                style={{ 
                  padding: '8px 16px', 
                  background: '#9c27b0', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Minify HTML
              </button>

              <button
                onClick={unescapeSmartQuotes}
                style={{
                  padding: '8px 16px',
                  background: '#795548',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Unescape \” to ”
              </button>
              
              <button 
                onClick={handleUploadClick}
                style={{ 
                  padding: '8px 16px', 
                  background: '#2196F3', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Upload HTML File
              </button>
              
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                accept=".html,.htm"
              />
              
              <button 
                onClick={loadExample}
                style={{ 
                  padding: '8px 16px', 
                  background: '#ff9800', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Load Example
              </button>
              
              <button 
                onClick={clearAll}
                style={{ 
                  padding: '8px 16px', 
                  background: '#f44336', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear All
              </button>
            </div>
            
            <div className="tab-size-control" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="tabSize" style={{ fontSize: '14px', color: '#666' }}>Indent Size:</label>
              <select 
                id="tabSize" 
                value={tabSize} 
                onChange={(e) => setTabSize(parseInt(e.target.value))}
                style={{ 
                  padding: '6px', 
                  borderRadius: '4px', 
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="2">2 spaces</option>
                <option value="4">4 spaces</option>
                <option value="8">8 spaces</option>
              </select>
            </div>
          </div>
          
          {error && (
            <div style={{ color: '#f44336', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px', marginBottom: '10px' }}>
              {error}
            </div>
          )}
          
          <div className="editor-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div className="input-section" style={{ position: 'relative', minHeight: '300px' }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '10px' }}>Input HTML</h3>
              <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', position: 'relative', height: '300px' }}>
                <TextareaWithLines
                  value={htmlInput}
                  onChange={(e) => setHtmlInput(e.target.value)}
                  placeholder="Paste your HTML code here..."
                  textareaRef={inputRef}
                  lineNumbersRef={inputLineNumbersRef}
                  onScroll={handleInputScroll}
                  style={{ minHeight: '300px' }}
                />
              </div>
            </div>
            
            {formattedHtml && (
              <div className="output-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '1.25rem', margin: 0 }}>Formatted HTML</h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleCopyOutput}
                      style={{
                        padding: '6px 12px',
                        background: '#607d8b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {copySuccess ? 'Copied!' : 'Copy'}
                    </button>
                    
                    <button
                      onClick={downloadFormattedHtml}
                      style={{
                        padding: '6px 12px',
                        background: '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Download
                    </button>
                  </div>
                </div>
                
                <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', position: 'relative', height: '300px' }}>
                  <PreWithLines
                    value={formattedHtml}
                    preRef={outputRef}
                    lineNumbersRef={outputLineNumbersRef}
                    onScroll={handleOutputScroll}
                    style={{ 
                      minHeight: '300px',
                      backgroundColor: '#f8f9fa',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About HTML Beautifier</h2>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            HTML Beautifier is a tool that helps you format your HTML code by properly indenting elements and organizing the structure.
            This makes your code more readable and easier to maintain.
          </p>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            Benefits of well-formatted HTML:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '20px', lineHeight: '1.6' }}>
            <li>Easier to read and understand</li>
            <li>Simpler to debug and maintain</li>
            <li>Improved collaboration with other developers</li>
            <li>Better code organization</li>
          </ul>
          <p style={{ lineHeight: '1.6' }}>
            This tool allows you to both beautify HTML for readability and minify it for production use.
            Minified HTML has all unnecessary whitespace removed to reduce file size.
          </p>
        </div>
      </div>
            
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .controls {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .button-container {
            flex-direction: column;
            width: 100%;
          }
          
          .button-container button {
            width: 100%;
          }
          
          .tab-size-control {
            margin-top: 10px;
            width: 100%;
          }
          
          .tab-size-control select {
            flex-grow: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default HtmlBeautifierPage; 
